import { SPLASH_LOADING_DEBOUNCE } from '@/config';
import { ApiConnectionError, ApiHttpError } from '@/lib/apiHttpError';
import { API_URL } from '@/lib/constants';
import type { paths } from '@/types/api';
import { useAuth } from '@auth';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import createFetchClient, {
  Middleware,
  MiddlewareCallbackParams,
  type Client,
} from 'openapi-fetch';
import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import { createContext, useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'wouter';

export interface LoginContextType {
  apiFetch: Client<paths, `${string}/${string}`>;
  userRole: string | null;
}

export const LoginContext = createContext<LoginContextType | null>(null);

export function LoginRequired({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback: React.ReactNode;
}) {
  const { isLoading, getAccessToken, user } = useAuth();

  const posthog = usePostHog();

  // Delay loading state to keep the splash screen visible
  const debouncedLoading = useDebounce(isLoading, SPLASH_LOADING_DEBOUNCE);

  const onRequest = useCallback(
    async ({ request }: MiddlewareCallbackParams) => {
      const accessToken = await getAccessToken();
      request.headers.set('Authorization', `Bearer ${accessToken}`);
      return request;
    },
    [getAccessToken],
  );

  const apiErrorMiddleware: Middleware = {
    onResponse: async ({ response }) => {
      const err = await ApiHttpError.create(response);
      if (err) {
        throw err;
      }
    },
    onError({ error }) {
      return new ApiConnectionError(error, 'An error occurred');
    },
  };

  const apiFetch = createFetchClient<paths>({ baseUrl: API_URL });
  apiFetch.use({ onRequest });
  apiFetch.use(apiErrorMiddleware);

  const userRole = useQuery({
    queryKey: ['v1', 'me'],
    queryFn: async () => apiFetch?.GET('/me') || null,
    select: (data) => {
      return data?.data?.data?.role || null;
    },
    enabled: !isLoading && !!apiFetch,
  });

  useEffect(() => {
    if (user && userRole.data) {
      posthog.identify(user.id, {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        role: userRole.data,
      });
    }
  }, [user, posthog, userRole.data]);

  const [, setLocation] = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('state');
  const redirectToUrlAfterLogin = redirectTo ? JSON.parse(redirectTo)?.returnTo : null;

  useEffect(() => {
    if (redirectToUrlAfterLogin) {
      setTimeout(() => {
        setLocation(redirectToUrlAfterLogin);
      }, 1000);
    }
  }, [redirectToUrlAfterLogin, setLocation, user]);

  if (debouncedLoading || !apiFetch) {
    return fallback;
  }
  return (
    <LoginContext.Provider value={{ apiFetch, userRole: userRole.data || null }}>
      {children}
    </LoginContext.Provider>
  );
}
